import { AppTableShell } from "@/components/blocks/app-table-shell";
import {
  InfiniteEntityList,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "@cmde/shadcn";
import { type InfiniteData, type UseInfiniteQueryResult } from "@tanstack/react-query";
import type { Key, ReactNode } from "react";
import { useTranslation } from "react-i18next";

export interface InfiniteTableColumnDef<T> {
  key: string;
  header: ReactNode;
  cell: (item: T, index: number) => ReactNode;
  width?: string | number;
  className?: string;
}

export function AppInfiniteTable<T, TPageParam = unknown, TError = Error>({
  queryResult,
  columns,
  getKey,
  errorContent,
  emptyContent,
  loadingContent,
  tableClassName,
}: {
  queryResult: UseInfiniteQueryResult<InfiniteData<T[], TPageParam> | undefined, TError>;
  columns: InfiniteTableColumnDef<T>[];
  getKey?: (item: T, index: number) => Key;
  loadingContent?: ReactNode;
  errorContent?: ReactNode;
  emptyContent?: ReactNode;
  tableClassName?: string;
}) {
  const { t } = useTranslation();

  const allItems = queryResult.data?.pages.flatMap((page) => page ?? []) ?? [];

  return (
    <AppTableShell>
      <InfiniteEntityList
        items={allItems}
        getKey={getKey}
        renderItem={(item) => (
          <>
            {columns.map((col, index) => (
              <TableCell key={col.key} className={col.className} style={{ width: col.width }}>
                {col.cell(item, index)}
              </TableCell>
            ))}
          </>
        )}
        renderList={(rows) => (
          <Table className={cn("w-full", tableClassName)}>
            <TableHeader>
              <TableRow>
                {columns.map((col) => (
                  <TableHead key={col.key} className={col.className} style={{ width: col.width }}>
                    {col.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow className="group" key={i}>
                  {row}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        onLoadMore={queryResult.fetchNextPage}
        hasNextPage={queryResult.hasNextPage}
        isFetchingNextPage={queryResult.isFetchingNextPage}
        isLoading={queryResult.isLoading}
        isError={queryResult.isError}
        loadingContent={loadingContent}
        errorContent={errorContent}
        emptyContent={emptyContent}
        loadMoreLabel={t("common.loadMore")}
        loadingMoreLabel={t("common.loadingMore")}
        noMoreItemsLabel={t("common.nothingMoreToLoad")}
      />
    </AppTableShell>
  );
}
